import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "379",
  "slug": "geoguessr-meta-bollards-road-lines-signs",
  "title": "Reading the Meta: How Pros Use Bollards, Road Lines, and Signs to Find Any Location",
  "excerpt": "Top location guessers barely look at the scenery. Here's how the 'meta' — bollards, road markings, signs, and small details — gives away a country in seconds.",
  "date": "2026-06-08",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1542169096-d626c9d082ab?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3aW5kaW5nJTIwY291bnRyeSUyMHJvYWR8ZW58MXwwfHx8MTc4MDkwNjI4OXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Expert location guessers rely on the 'meta' — small, consistent details like bollard shapes, road-line colours, which side of the road people drive on, license plates, and language on signs. These man-made clues often pin down a country far faster than the landscape does.",
  "content": [
    {
      "type": "paragraph",
      "text": "Watch a skilled location-guesser at work and something strange becomes clear: they often barely glance at the mountains, beaches, or skylines. Instead, their eyes dart to a roadside post, a painted line, a number plate, the wording on a sign. This is what the community calls the 'meta' — the collection of small, human-made details that quietly reveal where you are."
    },
    {
      "type": "paragraph",
      "text": "The landscape tells you the broad region. The meta narrows it down to a country, sometimes even a specific area. Here's how to start reading it."
    },
    {
      "type": "heading",
      "text": "Which Side of the Road?"
    },
    {
      "type": "paragraph",
      "text": "The very first thing to check is the direction of traffic. Roughly a third of the world drives on the left — including the UK, India, Japan, Australia, much of southern Africa, and many island nations — while the rest drives on the right. In one glance you've cut the map in half. Cars parked facing different directions, or the position of a lone oncoming vehicle, will tell you which it is."
    },
    {
      "type": "heading",
      "text": "Road Markings and Lines"
    },
    {
      "type": "paragraph",
      "text": "The colour and pattern of painted road lines vary by country and are a classic meta clue. As a rough guide:"
    },
    {
      "type": "list",
      "items": [
        "Yellow centre lines are common across the Americas and parts of Asia, while many countries in Europe use white centre lines.",
        "Outer edge lines, their colour, and whether they're solid or dashed differ from country to country.",
        "Some regions have distinctive markings — unusual crosswalk styles, painted shoulders, or particular dashed-line spacing.",
        "Road surface and quality also hint at a region's wealth and climate."
      ]
    },
    {
      "type": "paragraph",
      "text": "None of these is a guarantee on its own, but stacked together they build a strong case."
    },
    {
      "type": "heading",
      "text": "Bollards: The Guesser's Favourite"
    },
    {
      "type": "paragraph",
      "text": "Bollards — the small posts along roadsides marking the edge or guiding traffic — are beloved by serious players because their shape, colour, and reflector pattern are often standardised within a country but different across borders. Two neighbouring countries with near-identical scenery can have completely distinct bollards. Learning a few common designs is one of the highest-value things a new player can do."
    },
    {
      "type": "heading",
      "text": "Signs, Scripts, and Language"
    },
    {
      "type": "paragraph",
      "text": "Text is gold. Even if you can't read a language, the script narrows things fast: Cyrillic points to parts of Eastern Europe and Central Asia, Arabic script to North Africa and the Middle East, and the distinct writing systems of Thai, Korean, Japanese, or Greek each give the game away instantly."
    },
    {
      "type": "paragraph",
      "text": "When you can read the language, look at the small print: the font on official road signs, the format of distances, town names you can search later, and the style of business signage all help. Even the design of a 'stop' sign or a speed-limit sign carries clues."
    },
    {
      "type": "heading",
      "text": "License Plates and Vehicles"
    },
    {
      "type": "paragraph",
      "text": "Number plates vary in colour, shape, and the coloured strip many countries put on one side. The kinds of cars on the road matter too — certain brands and models are far more common in some markets than others, and the age and condition of vehicles hint at a country's wealth."
    },
    {
      "type": "heading",
      "text": "Don't Ignore the Sky and the Sun"
    },
    {
      "type": "paragraph",
      "text": "Not all the clues are man-made. The position of the sun is a quietly powerful one: at midday in the Northern Hemisphere the sun sits to the south, while in the Southern Hemisphere it sits to the north. Long shadows pointing a consistent direction can tell you which half of the world you're in. Vegetation, soil colour, and the angle of the light all add to the picture."
    },
    {
      "type": "heading",
      "text": "Clues From the Camera Itself"
    },
    {
      "type": "paragraph",
      "text": "Experienced players even read the camera car. The colour of the vehicle reflected in shiny surfaces, the presence or absence of a roof rack or antenna in the shot, and the generation of the camera (which changes image quality and tint) can all hint at when and where the imagery was captured. It's the most advanced layer of the meta, but it shows how much information is hiding in plain sight."
    },
    {
      "type": "heading",
      "text": "Putting It Together"
    },
    {
      "type": "paragraph",
      "text": "No single clue is decisive. The skill is in stacking them: drive on the left, plus white road lines, plus a particular bollard, plus Latin script with a familiar font — and suddenly you've gone from 'somewhere on Earth' to a confident country guess. The pros simply have a larger mental library of these details and check them faster."
    },
    {
      "type": "heading",
      "text": "How to Build the Skill"
    },
    {
      "type": "paragraph",
      "text": "Keep a note of the street-level clues you confuse and compare examples from both countries. Satellite games require a different approach: road signs and bollard details usually are not visible from above."
    }
  ]
};
